<script lang="ts">
	import type { Cache } from "$lib/serv/core/cache";

	import { ProductType, sortProductsByType, type GrocResults, type Store } from "$lib/def/landing";
	import { post$ } from "$lib/serv/core/auth";
	import { showDetail } from '$lib/ui/home/groc/pdp/Main.svelte';
	import { queryFrom } from "$lib/util/url";
	import { BehaviorSubject, Observable, catchError, combineLatest, distinctUntilChanged, map, of, share, startWith, switchMap } from "rxjs";

	import Grid from "$lib/ui/layout/Grid.svelte";
	import Product from "./Product.svelte";
import { thumbor } from "$lib/serv/image-details";

	export let urlSearchParams: URLSearchParams;
	export let selectedStore: { id: Store["id"] } | undefined;

	const subjPrams = new BehaviorSubject(urlSearchParams);
	const param$ = subjPrams.asObservable();
	$: subjPrams.next(urlSearchParams);
	
	const selectedStore$ = new BehaviorSubject(selectedStore);
	$: selectedStore$.next(selectedStore);
	
	let search$ = param$.pipe(
		map(p => p.get('search')),
		distinctUntilChanged(), // prevents search from reloading when PDP is opened (due to urlSearchParams update)
	)

	let retrySearch = new BehaviorSubject<boolean>(true);

	let results$: Observable<Cache<GrocResults>> = combineLatest([
		selectedStore$.pipe(distinctUntilChanged((a,b) => a?.id === b?.id)),
		search$,
		retrySearch
	]).pipe(
		switchMap(([store, text]) => {
			return post$<GrocResults>(`ca://grocery/search/`, { store_code: store?.id, text }, true).pipe(
				map((res) => ({
					loading: false,
					payload: res,
				})),
				startWith({ loading: true, payload: {} as GrocResults }),
				catchError((error) => of({ loading: false, error })),
			);
		}),
		map((cache) => {
			if ("error" in cache) return cache;

			return {
				...cache,
				payload: {
					...cache.payload,
					groceries: sortProductsByType(cache.payload.groceries, [ProductType.Combo])
				},
			}
		}),
		share(),
	);

</script>

{#if $results$.error}
	<div class="text-center" data-testid="groc-search-error-container">
		<div class="display-s bold m-0">Error searching groceries</div>
		<div class="body-m book grey">We can't process the request right now. Please try again later.</div>
		<button class="button m-small" on:click={() => retrySearch.next(true)}>
			<span class="icon-refresh-20"></span>&nbsp;Retry
		</button>
	</div>
{:else}
	{@const r = $results$.payload}
	{#if r?.aisles?.length === 0 && r?.groceries?.length === 0}
		<div class="body-m book grey text-center" data-testid="groc-search-results-title">{r?.title ?? `No results for ${$search$}`}</div>
	{:else}
		{#if r?.title}
			<h3 class="headline-m bold" data-testid="groc-search-results-title">{r.title}</h3>
		{/if}
		<Grid cache={$results$} type="aisles" key={(payload) => payload?.aisles} let:item={aisle} id="aisles">
			<a href="?{queryFrom(urlSearchParams).set('aisle', aisle.id.toString()).remove('search').remove('index')}" class="aisle item object-elevation-3pd" data-testid="groc-search-aisle">
				<img src={thumbor(aisle.icon, 350)} class="image" alt="icon">
				<span class="text label-m medium ellipses-2">{aisle.title}</span>
			</a>
		</Grid>
		<Grid cache={$results$} type="aisle" key={(payload) => payload?.groceries} let:item={product} id="aisles">
			<Product item={product} on:product={e => showDetail(e.detail)} />
		</Grid>
	{/if}
{/if}
